<template>
    <div class="countries-container">
        <div class="tree-controls">
            <a-space>
                <a-button size="small" @click="expandAll">
                    <template #icon>
                        <ExpandOutlined />
                    </template>
                </a-button>
                <a-button size="small" @click="collapseAll">
                    <template #icon>
                        <ShrinkOutlined />
                    </template>
                </a-button>
                <a-button size="small" @click="selectAllEnabledCountries">
                    <template #icon>
                        <CheckOutlined />
                    </template>
                    All
                </a-button>
                <a-button size="small" @click="clearAllSelections">
                    <template #icon>
                        <ClearOutlined />
                    </template>
                </a-button>
            </a-space>
            <div class="search-and-count">
                <a-input
                    v-model:value="searchKeyword"
                    placeholder="Search by country name or code..."
                    size="small"
                    style="width: 250px"
                    allowClear
                    @input="debouncedSearch"
                />
                <div class="selected-count">
                    Selected: {{ selectedCount }}
                </div>
            </div>
        </div>
        <div class="countries-tree">
            <a-tree 
                :key="`tree-${searchKeyword}-${treeKey}`"
                :checkedKeys="checkedKeys" 
                v-model:expandedKeys="expandedKeys" 
                :tree-data="displayTreeData"
                :checkable="true" 
                :selectable="false" 
                :check-strictly="false" 
                :load-data="undefined"
                :virtual="enabledCountriesCount > 500"
                @check="onCheck">
                <template #title="{ dataRef }">
                    <div class="tree-node-content">
                        <span v-if="dataRef.iso2" class="country-emoji w-4 h-3 inline-block" v-html="getCountryFlag(dataRef.iso2)"></span>
                        <span class="node-title" :class="{ 'disabled': !dataRef.isEnabled }" v-html="getHighlightedTitle(dataRef.title)"></span>
                        <span v-if="dataRef.isRegion" class="country-count">
                            ({{ dataRef.countryCount }} countries)
                        </span>
                    </div>
                </template>
            </a-tree>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, shallowRef, watch, nextTick } from 'vue';
import {
    ExpandOutlined,
    ShrinkOutlined,
    CheckOutlined,
    ClearOutlined
} from '@ant-design/icons-vue';
import { useZoneStore } from '@/store/modules/zone';
// @ts-ignore
import YxCountry from '@/data/countries';

interface Country {
    iso2: string;
    name: string;
    isEnabled: boolean;
    emoji?: string;
}

interface Region {
    id: string;
    name: string;
    countries: Country[];
}

interface TreeNode {
    key: string;
    title: string;
    emoji?: string;
    isRegion?: boolean;
    countryCount?: number;
    children?: TreeNode[];
    country?: Country;
    region?: Region;
    isEnabled?: boolean;
    disabled?: boolean;
    iso2?: string;
}

const zoneStore = useZoneStore();
const props = defineProps<{
    forbiddenCountries?: string[]
}>();

const regions = computed(() => zoneStore.getRegions);
const treeData = shallowRef<TreeNode[]>([]);
const checkedKeys = ref<string[]>([]);
const expandedKeys = ref<string[]>([]);
const initializedCountryCodes = ref<string[]>([]);
const searchKeyword = ref('');
const debouncedSearchKeyword = ref('');
const treeKey = ref(0);

// 缓存相关
const nodeCache = new Map<string, TreeNode>();
const flagCache = new Map<string, string>();
const highlightCache = new Map<string, string>();
const searchCache = new Map<string, TreeNode[]>();

// 搜索索引
interface SearchIndex {
    byName: Map<string, TreeNode[]>;
    byIso: Map<string, TreeNode[]>;
    allCountries: TreeNode[];
    nameTokens: Map<string, TreeNode[]>;
}

const searchIndex = ref<SearchIndex>({
    byName: new Map(),
    byIso: new Map(),
    allCountries: [],
    nameTokens: new Map()
});

// 计算选中的国家总数
const selectedCount = computed(() => {
    return checkedKeys.value.filter(key => !key.startsWith('region-')).length;
});

// 计算启用的国家总数（用于决定是否启用虚拟滚动）
const enabledCountriesCount = computed(() => {
    let count = 0;
    for (const region of regions.value) {
        count += region.countries.filter(country => country.isEnabled).length;
    }
    return count;
});

// 防抖搜索
let searchTimeout: NodeJS.Timeout;
const debouncedSearch = () => {
    clearTimeout(searchTimeout);
    searchTimeout = setTimeout(() => {
        debouncedSearchKeyword.value = searchKeyword.value;
        onSearchChange();
    }, 150); // 减少防抖时间
};

// 构建搜索索引
function buildSearchIndex() {
    const index: SearchIndex = {
        byName: new Map(),
        byIso: new Map(),
        allCountries: [],
        nameTokens: new Map()
    };

    for (const regionNode of treeData.value) {
        if (regionNode.children) {
            for (const countryNode of regionNode.children) {
                index.allCountries.push(countryNode);
                
                // 按国家名索引
                const nameKey = countryNode.title.toLowerCase();
                if (!index.byName.has(nameKey)) {
                    index.byName.set(nameKey, []);
                }
                index.byName.get(nameKey)!.push(countryNode);
                
                // 按ISO代码索引
                const iso2 = countryNode.iso2;
                if (iso2) {
                    const isoKey = iso2.toLowerCase();
                    if (!index.byIso.has(isoKey)) {
                        index.byIso.set(isoKey, []);
                    }
                    index.byIso.get(isoKey)!.push(countryNode);
                }
                
                // 按名称分词索引（支持部分匹配）
                const words = countryNode.title.toLowerCase().split(/\s+/);
                for (const word of words) {
                    if (word.length >= 2) { // 只索引长度>=2的词
                        if (!index.nameTokens.has(word)) {
                            index.nameTokens.set(word, []);
                        }
                        index.nameTokens.get(word)!.push(countryNode);
                    }
                }
            }
        }
    }

    searchIndex.value = index;
}

// 高效搜索函数
function fastSearch(keyword: string): TreeNode[] {
    const lowerKeyword = keyword.toLowerCase().trim();
    const matchedCountries = new Set<TreeNode>();

    // 1. 精确匹配ISO代码
    if (searchIndex.value.byIso.has(lowerKeyword)) {
        searchIndex.value.byIso.get(lowerKeyword)!.forEach(node => matchedCountries.add(node));
    }

    // 2. 精确匹配国家名
    if (searchIndex.value.byName.has(lowerKeyword)) {
        searchIndex.value.byName.get(lowerKeyword)!.forEach(node => matchedCountries.add(node));
    }

    // 3. 部分匹配ISO代码
    for (const [iso, countries] of searchIndex.value.byIso.entries()) {
        if (iso.includes(lowerKeyword)) {
            countries.forEach(node => matchedCountries.add(node));
        }
    }

    // 4. 词汇匹配
    for (const [token, countries] of searchIndex.value.nameTokens.entries()) {
        if (token.includes(lowerKeyword)) {
            countries.forEach(node => matchedCountries.add(node));
        }
    }

    // 5. 模糊匹配（降级处理）
    if (matchedCountries.size === 0) {
        for (const countryNode of searchIndex.value.allCountries) {
            if (countryNode.title.toLowerCase().includes(lowerKeyword) || 
                (countryNode.iso2 && countryNode.iso2.toLowerCase().includes(lowerKeyword))) {
                matchedCountries.add(countryNode);
            }
        }
    }

    return Array.from(matchedCountries);
}

// 优化后的过滤树数据
const displayTreeData = computed(() => {
    if (!debouncedSearchKeyword.value.trim()) {
        return treeData.value;
    }

    const keyword = debouncedSearchKeyword.value.toLowerCase().trim();
    
    // 使用搜索缓存
    if (searchCache.has(keyword)) {
        return buildTreeFromCountries(searchCache.get(keyword)!);
    }

    // 执行快速搜索
    const matchedCountries = fastSearch(keyword);
    
    // 构建树结构
    const result = buildTreeFromCountries(matchedCountries);
    
    // 缓存搜索结果
    searchCache.set(keyword, matchedCountries);
    
    // 限制缓存大小
    if (searchCache.size > 50) {
        const firstKey = searchCache.keys().next().value;
        if (firstKey) {
            searchCache.delete(firstKey);
        }
    }

    return result;
});

// 从匹配的国家构建树结构
function buildTreeFromCountries(countries: TreeNode[]): TreeNode[] {
    const regionMap = new Map<string, TreeNode[]>();

    // 按区域分组
    for (const country of countries) {
        // 找到该国家所属的区域
        for (const regionNode of treeData.value) {
            if (regionNode.children?.some(child => child.key === country.key)) {
                const regionKey = regionNode.key;
                if (!regionMap.has(regionKey)) {
                    regionMap.set(regionKey, []);
                }
                regionMap.get(regionKey)!.push(country);
                break;
            }
        }
    }

    // 构建过滤后的树
    const filtered: TreeNode[] = [];
    for (const [regionKey, regionCountries] of regionMap.entries()) {
        const originalRegion = treeData.value.find(r => r.key === regionKey);
        if (originalRegion) {
            filtered.push({
                key: originalRegion.key,
                title: originalRegion.title,
                isRegion: true,
                countryCount: regionCountries.length,
                region: originalRegion.region,
                disabled: false,
                children: regionCountries
            });
        }
    }

    return filtered;
}

// 缓存国旗获取
const getCountryFlag = (iso2: string): string => {
    if (flagCache.has(iso2)) {
        return flagCache.get(iso2)!;
    }
    const flag = YxCountry.flag(iso2);
    flagCache.set(iso2, flag);
    return flag;
};

// 优化高亮搜索词
const getHighlightedTitle = (title: string): string => {
    if (!debouncedSearchKeyword.value.trim()) {
        return title;
    }
    
    const cacheKey = `${title}-${debouncedSearchKeyword.value}`;
    if (highlightCache.has(cacheKey)) {
        return highlightCache.get(cacheKey)!;
    }
    
    const keyword = debouncedSearchKeyword.value.trim();
    const regex = new RegExp(`(${keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const highlighted = title.replace(regex, '<mark>$1</mark>');
    
    highlightCache.set(cacheKey, highlighted);
    return highlighted;
};

onMounted(() => {
    buildTreeData();
    expandAll();
});

// 监听禁用国家变化
watch(() => props.forbiddenCountries, () => {
    buildTreeData();
    treeKey.value++;
}, { deep: true });

// 监听区域数据变化
watch(() => regions.value, () => {
    buildTreeData();
    treeKey.value++;
}, { deep: true });

// 构建树形数据结构（使用缓存优化）
function buildTreeData() {
    const newTreeData: TreeNode[] = [];
    nodeCache.clear();
    for (const region of regions.value) {
        const regionKey = `region-${region.name}`;
        const children: TreeNode[] = [];
        for (const country of region.countries) {
            const isInitialized = initializedCountryCodes.value.includes(country.iso2);
            const isForbidden = props.forbiddenCountries?.includes(country.iso2) ?? false;
            
            const countryNode: TreeNode = {
                key: country.iso2,
                title: country.name,
                iso2: country.iso2,
                country: country,
                isRegion: false,
                isEnabled: isInitialized ? true : (isForbidden ? false : country.isEnabled),
                disabled: isForbidden ? true : (isInitialized ? false : !country.isEnabled)
            };
            
            children.push(countryNode);
            nodeCache.set(country.iso2, countryNode);
        }

        const regionNode: TreeNode = {
            key: regionKey,
            title: region.name,
            isRegion: true,
            countryCount: region.countries.length,
            region: region,
            disabled: false,
            children: children
        };

        newTreeData.push(regionNode);
        nodeCache.set(regionKey, regionNode);
    }

    treeData.value = newTreeData;
    
    // 构建完树数据后立即构建搜索索引
    buildSearchIndex();
}

// 展开所有节点
function expandAll() {
    expandedKeys.value = treeData.value.map(node => node.key);
}

// 收起所有节点
function collapseAll() {
    expandedKeys.value = [];
}

// 选择所有可用的国家
function selectAllEnabledCountries() {
    const allEnabledCountryKeys: string[] = [];
    for (const regionNode of treeData.value) {
        if (regionNode.children) {
            for (const countryNode of regionNode.children) {
                if (countryNode.isEnabled) {
                    allEnabledCountryKeys.push(countryNode.key);
                }
            }
        }
    }
    checkedKeys.value = allEnabledCountryKeys;
}

// 清空所有选择
function clearAllSelections() {
    checkedKeys.value = [];
}

// 处理树节点选中状态变化
function onCheck(checkedKeysValue: string[] | { checked: string[]; halfChecked: string[] }) {
    const keys = Array.isArray(checkedKeysValue) ? checkedKeysValue : checkedKeysValue.checked;
    
    // 获取当前显示在树中的所有国家 key（包括区域节点）
    const displayedCountryKeys = new Set<string>();
    const displayedRegionKeys = new Set<string>();
    
    function collectKeys(nodes: TreeNode[]) {
        for (const node of nodes) {
            if (node.isRegion) {
                displayedRegionKeys.add(node.key);
                if (node.children) {
                    collectKeys(node.children);
                }
            } else {
                displayedCountryKeys.add(node.key);
            }
        }
    }
    
    collectKeys(displayTreeData.value);
    
    // 过滤掉禁用的节点，但保留初始化的节点
    const enabledKeys = keys.filter(key => {
        // 区域节点直接保留
        if (key.startsWith('region-')) {
            return true;
        }
        // 国家节点需要检查是否启用
        const node = nodeCache.get(key);
        return node && (initializedCountryCodes.value.includes(key) || !node.disabled);
    });
    
    // 从之前的 checkedKeys 中移除所有在当前显示树中的项（因为它们的状态由新的 checkedKeysValue 决定）
    // 但保留所有不在当前显示树中的项（这些是搜索结果外的已选国家，应该保持选中状态）
    const previousSelected = checkedKeys.value.filter(key => {
        // 如果是区域节点
        if (key.startsWith('region-')) {
            // 如果当前显示树中有这个区域，则移除（使用新的选中状态）；否则保留
            return !displayedRegionKeys.has(key);
        }
        // 如果是国家节点，不在当前显示树中的应该保留
        return !displayedCountryKeys.has(key);
    });
    
    // 合并：之前选中的（不在当前树中的）+ 当前树中选中的
    const newCheckedKeys = [...previousSelected, ...enabledKeys];
    
    // 去重并更新
    checkedKeys.value = Array.from(new Set(newCheckedKeys));
}

// 获取所有选中的国家
function getSelectedCountries(): Country[] {
    const selectedCountries: Country[] = [];

    for (const key of checkedKeys.value) {
        if (!key.startsWith('region-')) {
            const node = nodeCache.get(key);
            if (node?.country) {
                selectedCountries.push(node.country);
            }
        }
    }

    return selectedCountries;
}

// 获取选中国家的代码数组
function getSelectedCountryCodes(): string[] {
    return checkedKeys.value.filter(key => !key.startsWith('region-'));
}

// 初始化选中的国家
function initSelectedCountries(initialCountries: Country[] | string[]) {
    if (!initialCountries || initialCountries.length === 0) {
        checkedKeys.value = [];
        initializedCountryCodes.value = [];
        return;
    }

    let countryCodes: string[] = [];

    if (typeof initialCountries[0] === 'string') {
        countryCodes = initialCountries as string[];
    } else {
        countryCodes = (initialCountries as Country[]).map(country => country.iso2);
    }

    // 过滤出存在的国家代码
    const validCodes = countryCodes.filter(code => nodeCache.has(code));

    initializedCountryCodes.value = validCodes;
    buildTreeData();
    checkedKeys.value = validCodes;
    
    // 展开包含选中国家的区域
    const regionsToExpand = new Set<string>();
    for (const regionNode of treeData.value) {
        if (regionNode.children?.some(countryNode => validCodes.includes(countryNode.key))) {
            regionsToExpand.add(regionNode.key);
        }
    }
    expandedKeys.value = Array.from(regionsToExpand);
}

// 获取选中国家按区域分组的数据
function getSelectedCountriesByRegion(): Record<string, Country[]> {
    const result: Record<string, Country[]> = {};

    for (const regionNode of treeData.value) {
        if (regionNode.children && regionNode.region) {
            const selectedInRegion: Country[] = [];
            
            for (const countryNode of regionNode.children) {
                if (checkedKeys.value.includes(countryNode.key) && 
                    countryNode.country && 
                    countryNode.isEnabled) {
                    selectedInRegion.push(countryNode.country);
                }
            }

            if (selectedInRegion.length > 0) {
                result[regionNode.region.name] = selectedInRegion;
            }
        }
    }

    return result;
}

// 根据区域选择国家
function selectCountriesByRegion(regionName: string, select: boolean = true) {
    const regionNode = treeData.value.find(node => node.region?.name === regionName);

    if (regionNode?.children) {
        const regionCountryCodes = regionNode.children
            .filter(child => child.isEnabled)
            .map(child => child.key);

        if (select) {
            const newCheckedKeys = new Set(checkedKeys.value);
            regionCountryCodes.forEach(code => newCheckedKeys.add(code));
            checkedKeys.value = Array.from(newCheckedKeys);
        } else {
            const codesToRemove = new Set(regionCountryCodes);
            checkedKeys.value = checkedKeys.value.filter(key => !codesToRemove.has(key));
        }
    }
}

// 获取区域的选中状态
function getRegionSelectStatus(regionName: string): 'all' | 'partial' | 'none' {
    const regionNode = treeData.value.find(node => node.region?.name === regionName);

    if (!regionNode?.children) return 'none';

    const enabledCountryCodes = regionNode.children
        .filter(child => child.isEnabled)
        .map(child => child.key);
    
    const selectedInRegion = enabledCountryCodes.filter(code =>
        checkedKeys.value.includes(code)
    );

    if (selectedInRegion.length === 0) return 'none';
    if (selectedInRegion.length === enabledCountryCodes.length) return 'all';
    return 'partial';
}

// 搜索变化处理
function onSearchChange() {
    if (!debouncedSearchKeyword.value.trim()) {
        expandAll();
    } else {
        nextTick(() => {
            expandedKeys.value = displayTreeData.value.map(node => node.key);
        });
    }
    
    // 清除高亮缓存
    highlightCache.clear();
}

// 暴露给父组件的方法和数据
defineExpose({
    initSelectedCountries,
    getSelectedCountries,
    getSelectedCountryCodes,
    clearAllSelections,
    selectAllEnabledCountries,
    getSelectedCountriesByRegion,
    selectCountriesByRegion,
    getRegionSelectStatus,
    expandAll,
    collapseAll,
    selectedCount,
    regions,
    checkedKeys
});
</script>

<style scoped>
.countries-container {
    border: 1px solid #d9d9d9;
    border-radius: 6px;
    background-color: #fafafa;
}

.tree-controls {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 16px;
    background-color: #f8f9fa;
    border-bottom: 1px solid #e9ecef;
}

.search-and-count {
    display: flex;
    align-items: center;
    gap: 16px;
}

.selected-count {
    font-size: 14px;
    color: #666;
    font-weight: 500;
    white-space: nowrap;
}

.filtered-info {
    color: #1890ff;
    font-weight: 400;
}

.countries-tree {
    height: 60vh;
    overflow-y: auto;
    padding: 8px;
    background-color: #fff;
}

.tree-node-content {
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 1;
}

.country-emoji {
    font-size: 16px;
    flex-shrink: 0;
}

.node-title {
    flex: 1;
    font-size: 14px;
    color: #333;
}

.node-title.disabled {
    color: #999;
    cursor: not-allowed;
}

.country-count {
    font-size: 12px;
    color: #999;
    margin-left: auto;
}

/* 搜索高亮样式 */
:deep(mark) {
    background-color: #fff3cd;
    color: #856404;
    padding: 1px 2px;
    border-radius: 2px;
    font-weight: 500;
}

/* 响应式设计 */
@media (max-width: 768px) {
    .tree-controls {
        flex-direction: column;
        gap: 8px;
        align-items: stretch;
    }

    .search-and-count {
        flex-direction: column;
        gap: 8px;
        align-items: stretch;
    }

    .search-and-count .ant-input {
        width: 100% !important;
    }

    .selected-count {
        text-align: center;
        font-size: 13px;
    }

    .countries-container {
        max-height: 500px;
    }

    .countries-tree {
        max-height: 400px;
    }
}

@media (max-width: 480px) {
    .tree-controls {
        padding: 8px 12px;
    }
    
    .search-and-count .ant-input {
        font-size: 14px;
    }
    
    .selected-count {
        font-size: 12px;
    }
}

/* Tree 组件样式定制 */
:deep(.ant-tree) {
    background: transparent;
}

:deep(.ant-tree-node-content-wrapper) {
    padding: 4px 8px;
    border-radius: 4px;
    transition: all 0.2s ease;
}

:deep(.ant-tree-node-content-wrapper:hover) {
    background-color: #f0f8ff;
}

:deep(.ant-tree-checkbox) {
    margin-right: 8px;
}

:deep(.ant-tree-title) {
    flex: 1;
}

/* 区域节点样式 */
:deep(.ant-tree-treenode[data-level="0"]) {
    font-weight: 600;
}

:deep(.ant-tree-treenode[data-level="0"] .ant-tree-node-content-wrapper) {
    background-color: #f8f9fa;
    border: 1px solid #e9ecef;
    margin-bottom: 4px;
}

:deep(.ant-tree-treenode[data-level="0"] .ant-tree-node-content-wrapper:hover) {
    background-color: #e3f2fd;
    border-color: #bbdefb;
}

/* 国家节点样式 */
:deep(.ant-tree-treenode[data-level="1"] .ant-tree-node-content-wrapper) {
    margin-left: 8px;
    margin-bottom: 2px;
}

/* 缩进线样式 */
:deep(.ant-tree-indent-unit) {
    width: 20px;
}

:deep(.ant-tree-switcher) {
    width: 20px;
    height: 20px;
    line-height: 20px;
}

/* 禁用状态样式 */
:deep(.ant-tree-checkbox-disabled) {
    cursor: not-allowed;
}

:deep(.ant-tree-checkbox-disabled .ant-tree-checkbox-inner) {
    background-color: #f5f5f5;
    border-color: #d9d9d9;
}

:deep(.ant-tree-node-content-wrapper-disabled) {
    cursor: not-allowed;
    color: #999;
}
</style>